import { useEffect, useRef } from 'react';
import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { TranslateHtml } from '@change-corgi/core-react/i18n';

import { MAX_DURATION_SEC } from '../../../../../VideoRecorder/components/RecorderTimeLimit';
import { MontageVideo } from '../../../VideoFirstAsk/components/MontageVideo';
import { BoltIcon } from '../../../VideoFirstAsk/icons';
import type { ConsolidatedAskPhase } from '../../hooks/useVideoFirstConsolidatedAsk';
import { RecordingBadge } from '../RecordingBadge';
import { TakePlayback } from '../TakePlayback';

type Props = {
	phase: ConsolidatedAskPhase;
	stream: MediaStream | null;
	secondsRemaining: number;
	blob: Blob | null;
	takeLengthSec: number | undefined;
};

/**
 * The 3:4 window everything in this arm happens inside: the montage, then the live camera, then the
 * take.
 *
 * The box never changes size and the live `<video>` is always mounted underneath, so granting permission
 * is a crossfade rather than a mount - nothing on the page moves at the moment the camera comes on. That
 * is the point of the arm, so resist making any layer conditional on `phase` alone.
 */
export const CameraSurface: React.FC<Props> = ({ phase, stream, secondsRemaining, blob, takeLengthSec }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const isMontageFaded = phase !== 'prime';
	// Stays mounted while it fades, because a crossfade needs both layers present, and is dropped at
	// review - by then the take covers the surface and there is no reason for a Mux player to keep
	// decoding behind it. Deleting a take brings it back.
	const isMontageMounted = phase !== 'review';
	const isValuePropFaded = phase === 'recording' || phase === 'review';

	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.srcObject = stream;
		}
	}, [stream]);

	return (
		<div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-black" data-testid="camera-surface">
			{/* Mirrored, as every front-facing camera preview is: an unmirrored one reads as backwards. */}
			<video
				ref={videoRef}
				autoPlay
				muted
				playsInline
				className="absolute inset-0 h-full w-full scale-x-[-1] object-cover"
				data-testid="recorder-video-element"
			/>

			{isMontageMounted && (
				<div
					className={cn(
						'absolute inset-0 transition-opacity duration-500 ease-out motion-reduce:transition-none',
						isMontageFaded ? 'opacity-0' : 'opacity-100',
					)}
					aria-hidden={isMontageFaded}
				>
					<MontageVideo />
				</div>
			)}

			{phase === 'review' && blob && <TakePlayback blob={blob} takeLengthSec={takeLengthSec} />}

			{phase === 'recording' && <RecordingBadge secondsRemaining={secondsRemaining} />}

			{/*
			 * The value prop, over its own scrim. Fades out once there is nothing left to promise - a take is
			 * either rolling or waiting to be reviewed - and stays out of the way of the playback control.
			 */}
			<div
				className={cn(
					'pointer-events-none absolute inset-0 transition-opacity duration-300 motion-reduce:transition-none',
					isValuePropFaded ? 'opacity-0' : 'opacity-100',
				)}
				/* Out of the accessibility tree once faded, as the montage layer is: `opacity-0` alone would
				   leave a screen reader reading the promise of a video next to the one just recorded. */
				aria-hidden={isValuePropFaded}
			>
				<div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/75 to-transparent" />
				<div className="absolute inset-x-0 bottom-8 flex items-center justify-center gap-1.5 px-4">
					<BoltIcon />
					<Text as="p" variant="body" size="small" className="text-center text-white">
						<TranslateHtml
							value="corgi.petition_actions.video_first_ask.value_prop"
							replacements={{ maxDuration: MAX_DURATION_SEC }}
						/>
					</Text>
				</div>
			</div>
		</div>
	);
};
